import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePortfolio } from '../../context/PortfolioContext';
import { reconcilePortfolio, applyReconcileFixes } from '../../services/api';
import { GitCompare, Upload, CheckCircle, AlertTriangle, XCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

// ── Types ──────────────────────────────────────────────────────────────

interface ParsedPosition {
  ticker: string;
  shares: number;
  price: number;
}

interface ReconciliationResultItem {
  ticker: string;
  status: 'matched' | 'mismatch' | 'missing_in_portfolio' | 'missing_in_broker';
  broker_shares: number;
  portfolio_shares: number;
  shares_diff: number;
  broker_price: number;
  portfolio_price: number;
}

interface ReconciliationSummary {
  matched: number;
  mismatched: number;
  missing: number;
  total: number;
}

interface SuggestedFix {
  ticker: string;
  action: string;
  shares: number;
  price: number;
  reason: string;
}

interface ReconciliationResponse {
  reconciliation: {
    results: ReconciliationResultItem[];
    summary: ReconciliationSummary;
  };
  suggested_fixes: SuggestedFix[];
}

interface ApplyFixesResponse {
  success: boolean;
  transactions_created: number;
}

// ── CSV Parser ─────────────────────────────────────────────────────────

function parseCSV(raw: string): ParsedPosition[] {
  const lines = raw.trim().split('\n').filter(l => l.trim().length > 0);
  if (lines.length < 2) return [];

  const headerLine = lines[0].toLowerCase();
  const headers = headerLine.split(',').map(h => h.trim());

  const tickerIdx = headers.findIndex(h =>
    ['ticker', 'symbol', 'sym'].includes(h)
  );
  const sharesIdx = headers.findIndex(h =>
    ['shares', 'quantity', 'qty', 'amount'].includes(h)
  );
  const priceIdx = headers.findIndex(h =>
    ['price', 'last price', 'last', 'current price', 'market value'].includes(h)
  );

  if (tickerIdx === -1 || sharesIdx === -1) return [];

  const positions: ParsedPosition[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map(c => c.trim());
    const ticker = cols[tickerIdx]?.toUpperCase();
    const shares = parseFloat(cols[sharesIdx]?.replace(/[^0-9.-]/g, '') || '0');
    const price = priceIdx !== -1
      ? parseFloat(cols[priceIdx]?.replace(/[^0-9.-]/g, '') || '0')
      : 0;

    if (ticker && !isNaN(shares) && shares !== 0) {
      positions.push({ ticker, shares, price });
    }
  }

  return positions;
}

// ── Step Indicator ─────────────────────────────────────────────────────

const STEPS = ['Upload', 'Preview', 'Results', 'Confirmation'];

function StepIndicator({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center justify-center gap-0">
      {STEPS.map((label, idx) => {
        const isCompleted = idx < currentStep;
        const isActive = idx === currentStep;
        const isPending = idx > currentStep;

        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  isCompleted
                    ? 'bg-emerald-500 text-white'
                    : isActive
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  idx + 1
                )}
              </div>
              <span
                className={`mt-1 text-xs font-medium ${
                  isActive ? 'text-blue-600' : isPending ? 'text-slate-400' : 'text-emerald-600'
                }`}
              >
                {label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`w-16 h-0.5 mb-5 ${
                  idx < currentStep ? 'bg-emerald-500' : 'bg-slate-200'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Status badge helpers ───────────────────────────────────────────────

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'matched':
      return 'bg-emerald-100 text-emerald-700';
    case 'mismatch':
      return 'bg-amber-100 text-amber-700';
    case 'missing_in_portfolio':
      return 'bg-red-100 text-red-700';
    case 'missing_in_broker':
      return 'bg-orange-100 text-orange-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function statusRowClass(status: string): string {
  switch (status) {
    case 'matched':
      return 'bg-emerald-50';
    case 'mismatch':
      return 'bg-amber-50';
    case 'missing_in_portfolio':
      return 'bg-red-50';
    case 'missing_in_broker':
      return 'bg-orange-50';
    default:
      return '';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'matched':
      return 'Matched';
    case 'mismatch':
      return 'Mismatch';
    case 'missing_in_portfolio':
      return 'Missing in Portfolio';
    case 'missing_in_broker':
      return 'Missing in Broker';
    default:
      return status;
  }
}

// ── Main Component ─────────────────────────────────────────────────────

const ReconciliationView: React.FC = () => {
  const { currentUser } = useAuth();
  const { activePortfolioId } = usePortfolio();

  // Wizard state
  const [step, setStep] = useState(0);

  // Step 1 state
  const [brokerFormat, setBrokerFormat] = useState('generic');
  const [csvText, setCsvText] = useState('');
  const [parsedPositions, setParsedPositions] = useState<ParsedPosition[]>([]);
  const [parseError, setParseError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 3 state
  const [reconciliationData, setReconciliationData] = useState<ReconciliationResponse | null>(null);
  const [selectedFixes, setSelectedFixes] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Step 4 state
  const [applyResult, setApplyResult] = useState<ApplyFixesResponse | null>(null);

  // ── Guard ──────────────────────────────────────────────────────────────
  if (!activePortfolioId) {
    return (
      <div className="h-full overflow-y-auto bg-slate-50">
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col items-center justify-center py-20">
            <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
            <h2 className="text-xl font-bold text-slate-700 mb-2">No Portfolio Selected</h2>
            <p className="text-slate-500">Select a portfolio first to use the Broker Reconciliation tool.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── File upload handler ────────────────────────────────────────────────

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setCsvText(text);
    };
    reader.readAsText(file);
  };

  // ── Step 1 → Step 2: Parse ────────────────────────────────────────────

  const handleParse = () => {
    setParseError('');
    const positions = parseCSV(csvText);
    if (positions.length === 0) {
      setParseError(
        'Could not parse any positions. Make sure your CSV has headers including ticker/symbol and shares/quantity columns.'
      );
      return;
    }
    setParsedPositions(positions);
    setStep(1);
  };

  // ── Step 2 → Step 3: Reconcile ────────────────────────────────────────

  const handleReconcile = async () => {
    if (!currentUser || !activePortfolioId) return;

    setIsLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const apiPositions = parsedPositions.map(p => ({
        ticker: p.ticker,
        shares: p.shares,
        price: p.price,
      }));

      const data: ReconciliationResponse = await reconcilePortfolio(
        token,
        activePortfolioId,
        apiPositions,
        brokerFormat
      );

      setReconciliationData(data);
      setSelectedFixes(new Set());
      setStep(2);
    } catch (err: any) {
      toast.error(err?.message || 'Reconciliation failed.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 3 → Step 4: Apply fixes ──────────────────────────────────────

  const handleApplyFixes = async () => {
    if (!currentUser || !activePortfolioId || !reconciliationData) return;

    const fixesToApply = reconciliationData.suggested_fixes.filter((_, i) =>
      selectedFixes.has(i)
    );

    if (fixesToApply.length === 0) {
      toast.error('Select at least one fix to apply.');
      return;
    }

    setIsLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const result: ApplyFixesResponse = await applyReconcileFixes(
        token,
        activePortfolioId,
        fixesToApply
      );

      setApplyResult(result);
      setStep(3);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to apply fixes.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Reset wizard ───────────────────────────────────────────────────────

  const resetWizard = () => {
    setStep(0);
    setCsvText('');
    setBrokerFormat('generic');
    setParsedPositions([]);
    setParseError('');
    setReconciliationData(null);
    setSelectedFixes(new Set());
    setApplyResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Fix checkbox toggle ────────────────────────────────────────────────

  const toggleFix = (idx: number) => {
    setSelectedFixes(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const toggleAllFixes = () => {
    if (!reconciliationData) return;
    const total = reconciliationData.suggested_fixes.length;
    if (selectedFixes.size === total) {
      setSelectedFixes(new Set());
    } else {
      setSelectedFixes(new Set(Array.from({ length: total }, (_, i) => i)));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <GitCompare className="w-7 h-7 text-blue-600" />
              Broker Reconciliation
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Compare your portfolio positions against your broker statement
            </p>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <StepIndicator currentStep={step} />
        </div>

        {/* ─── Step 1: Upload ─────────────────────────────────────── */}
        {step === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-1">
                <Upload className="w-5 h-5 text-blue-500" />
                Upload Broker Positions
              </h2>
              <p className="text-sm text-slate-500">
                Paste your broker CSV data or upload a file. We will parse it and compare with your portfolio.
              </p>
            </div>

            {/* Broker format */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Broker Format
              </label>
              <select
                value={brokerFormat}
                onChange={e => setBrokerFormat(e.target.value)}
                className="bg-white border border-slate-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="generic">Generic CSV</option>
                <option value="fidelity">Fidelity</option>
                <option value="schwab">Schwab</option>
                <option value="ibkr">Interactive Brokers (IBKR)</option>
              </select>
            </div>

            {/* Textarea */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                CSV Data
              </label>
              <textarea
                value={csvText}
                onChange={e => setCsvText(e.target.value)}
                placeholder={`Ticker,Shares,Price\nAAPL,100,175.50\nMSFT,50,380.25\nGOOGL,30,142.00`}
                className="font-mono bg-slate-50 border border-slate-300 rounded-lg p-4 w-full h-48 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            {/* File upload */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Or Upload a File
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="block text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            {/* Error */}
            {parseError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                <XCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{parseError}</span>
              </div>
            )}

            {/* Action */}
            <div className="flex justify-end">
              <button
                onClick={handleParse}
                disabled={!csvText.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg px-6 py-3 font-bold text-sm flex items-center gap-2 transition-colors"
              >
                Parse &amp; Compare
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 2: Preview ────────────────────────────────────── */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800 mb-1">
                Parsed Positions Preview
              </h2>
              <p className="text-sm text-slate-500">
                {parsedPositions.length} position{parsedPositions.length !== 1 ? 's' : ''} found. Review before running reconciliation.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">#</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Ticker</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">Shares</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">Price</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedPositions.map((pos, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2.5 px-4 text-slate-400">{idx + 1}</td>
                      <td className="py-2.5 px-4 font-bold text-slate-800">{pos.ticker}</td>
                      <td className="py-2.5 px-4 text-right font-mono">{pos.shares.toLocaleString()}</td>
                      <td className="py-2.5 px-4 text-right font-mono">
                        {pos.price > 0 ? `$${pos.price.toFixed(2)}` : '--'}
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono">
                        {pos.price > 0
                          ? `$${(pos.shares * pos.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div className="flex justify-between">
              <button
                onClick={() => setStep(0)}
                className="text-slate-600 hover:text-slate-800 rounded-lg px-5 py-3 font-semibold text-sm flex items-center gap-2 border border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={handleReconcile}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg px-6 py-3 font-bold text-sm flex items-center gap-2 transition-colors"
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Reconciling...
                  </>
                ) : (
                  <>
                    Run Reconciliation
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 3: Results ────────────────────────────────────── */}
        {step === 2 && reconciliationData && (
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border-2 border-emerald-200 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                  <span className="text-sm font-medium text-slate-600">Matched</span>
                </div>
                <p className="text-3xl font-bold text-emerald-600">
                  {reconciliationData.reconciliation.summary.matched}
                </p>
              </div>
              <div className="bg-white rounded-xl border-2 border-amber-200 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-2">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                  <span className="text-sm font-medium text-slate-600">Mismatched</span>
                </div>
                <p className="text-3xl font-bold text-amber-600">
                  {reconciliationData.reconciliation.summary.mismatched}
                </p>
              </div>
              <div className="bg-white rounded-xl border-2 border-red-200 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle className="w-6 h-6 text-red-500" />
                  <span className="text-sm font-medium text-slate-600">Missing</span>
                </div>
                <p className="text-3xl font-bold text-red-600">
                  {reconciliationData.reconciliation.summary.missing}
                </p>
              </div>
              <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-2">
                  <GitCompare className="w-6 h-6 text-slate-500" />
                  <span className="text-sm font-medium text-slate-600">Total</span>
                </div>
                <p className="text-3xl font-bold text-slate-700">
                  {reconciliationData.reconciliation.summary.total}
                </p>
              </div>
            </div>

            {/* Results table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-bold text-slate-800 mb-4">Reconciliation Results</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">Ticker</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">Status</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Broker Shares</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Portfolio Shares</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconciliationData.reconciliation.results.map((item, idx) => (
                      <tr key={idx} className={`border-b border-slate-100 ${statusRowClass(item.status)}`}>
                        <td className="py-2.5 px-4 font-bold text-slate-800">{item.ticker}</td>
                        <td className="py-2.5 px-4">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(item.status)}`}
                          >
                            {statusLabel(item.status)}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono">{item.broker_shares}</td>
                        <td className="py-2.5 px-4 text-right font-mono">{item.portfolio_shares}</td>
                        <td className="py-2.5 px-4 text-right font-mono">
                          {item.shares_diff !== 0 ? (
                            <span className={item.shares_diff > 0 ? 'text-emerald-600' : 'text-red-600'}>
                              {item.shares_diff > 0 ? '+' : ''}
                              {item.shares_diff}
                            </span>
                          ) : (
                            <span className="text-slate-400">--</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Suggested fixes */}
            {reconciliationData.suggested_fixes.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-slate-800">Suggested Fixes</h2>
                  <button
                    onClick={toggleAllFixes}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {selectedFixes.size === reconciliationData.suggested_fixes.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 w-10">
                          <input
                            type="checkbox"
                            checked={
                              reconciliationData.suggested_fixes.length > 0 &&
                              selectedFixes.size === reconciliationData.suggested_fixes.length
                            }
                            onChange={toggleAllFixes}
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Ticker</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Action</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Shares</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Price</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconciliationData.suggested_fixes.map((fix, idx) => (
                        <tr
                          key={idx}
                          className={`border-b border-slate-100 cursor-pointer transition-colors ${
                            selectedFixes.has(idx) ? 'bg-blue-50' : 'hover:bg-slate-50'
                          }`}
                          onClick={() => toggleFix(idx)}
                        >
                          <td className="py-2.5 px-4">
                            <input
                              type="checkbox"
                              checked={selectedFixes.has(idx)}
                              onChange={() => toggleFix(idx)}
                              onClick={e => e.stopPropagation()}
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="py-2.5 px-4 font-bold text-slate-800">{fix.ticker}</td>
                          <td className="py-2.5 px-4">
                            <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 capitalize">
                              {fix.action}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono">{fix.shares}</td>
                          <td className="py-2.5 px-4 text-right font-mono">
                            ${fix.price.toFixed(2)}
                          </td>
                          <td className="py-2.5 px-4 text-slate-600">{fix.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between">
              <button
                onClick={resetWizard}
                className="text-slate-600 hover:text-slate-800 rounded-lg px-5 py-3 font-semibold text-sm flex items-center gap-2 border border-slate-300 hover:bg-slate-50 transition-colors"
              >
                Start Over
              </button>
              {reconciliationData.suggested_fixes.length > 0 && (
                <button
                  onClick={handleApplyFixes}
                  disabled={isLoading || selectedFixes.size === 0}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg px-6 py-3 font-bold text-sm flex items-center gap-2 transition-colors"
                >
                  {isLoading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      Apply Selected Fixes ({selectedFixes.size})
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ─── Step 4: Confirmation ───────────────────────────────── */}
        {step === 3 && applyResult && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle className="w-20 h-20 text-emerald-500 mb-6" />
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Reconciliation Complete
              </h2>
              <p className="text-slate-500 mb-1 text-lg">
                Fixes have been successfully applied to your portfolio.
              </p>
              <p className="text-slate-600 font-semibold text-lg mb-8">
                {applyResult.transactions_created} transaction{applyResult.transactions_created !== 1 ? 's' : ''} created.
              </p>
              <button
                onClick={resetWizard}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-8 py-3 font-bold text-sm transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReconciliationView;
